/**
 * Analytics Service
 * Tracks events for product analytics
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export type EventType = 
  | 'onboarding'
  | 'action'
  | 'import'
  | 'followup'
  | 'stage_change'
  | 'lead'
  | 'weekly_report'
  | 'referral'
  | 'invite'
  | 'whatsapp';

export interface AnalyticsEvent {
  event_type: EventType;
  event_name: string;
  properties?: Record<string, any>;
}

/**
 * Track analytics event
 */
export async function trackEvent(
  workspaceId: string | null,
  userId: string | null,
  event: AnalyticsEvent
): Promise<void> {
  try {
    // Use RPC function if available
    await supabase.rpc('track_event', {
      p_workspace_id: workspaceId,
      p_user_id: userId,
      p_event_type: event.event_type,
      p_event_name: event.event_name,
      p_properties: event.properties || {},
    });
  } catch (error: any) {
    // Fallback to direct insert if RPC doesn't exist
    if (error?.code === '42883') {
      try {
        await supabase.from('analytics_events').insert({
          workspace_id: workspaceId,
          user_id: userId,
          event_type: event.event_type,
          event_name: event.event_name,
          properties: event.properties || {},
        } as any);
      } catch (insertError) {
        logger.warn('[analytics] Failed to track event (fallback)', insertError);
      }
    } else {
      logger.warn('[analytics] Failed to track event', error);
    }
  }
}

/**
 * Track onboarding events
 */
export function trackOnboardingStarted(userId: string, workspaceId: string) {
  return trackEvent(workspaceId, userId, {
    event_type: 'onboarding',
    event_name: 'onboarding_started',
  });
}

export function trackOnboardingStepCompleted(
  userId: string,
  workspaceId: string,
  step: number
) {
  return trackEvent(workspaceId, userId, {
    event_type: 'onboarding',
    event_name: 'onboarding_step_completed',
    properties: { step },
  });
}

/**
 * Track action events
 */
export function trackActionCompleted(
  userId: string,
  workspaceId: string,
  actionType: string,
  outcome: string,
  leadId?: string
) {
  return trackEvent(workspaceId, userId, {
    event_type: 'action',
    event_name: 'action_completed',
    properties: { actionType, outcome, leadId },
  });
}

/**
 * Track import events
 */
export function trackImportStarted(userId: string, workspaceId: string) {
  return trackEvent(workspaceId, userId, {
    event_type: 'import',
    event_name: 'import_started',
  });
}

export function trackImportCompleted(
  userId: string,
  workspaceId: string,
  created: number,
  updated: number,
  skipped: number,
  errors: number
) {
  return trackEvent(workspaceId, userId, {
    event_type: 'import',
    event_name: 'import_completed',
    properties: { created, updated, skipped, errors },
  });
}

/**
 * Track followup events
 */
export function trackFollowupScheduled(
  userId: string,
  workspaceId: string,
  leadId: string,
  days: number
) {
  return trackEvent(workspaceId, userId, {
    event_type: 'followup',
    event_name: 'followup_scheduled',
    properties: { leadId, days },
  });
}

/**
 * Track win card share
 */
export function trackWinCardShared(
  userId: string,
  workspaceId: string,
  format: 'story' | 'feed'
) {
  return trackEvent(workspaceId, userId, {
    event_type: 'weekly_report',
    event_name: 'win_card_shared',
    properties: { format },
  });
}

/**
 * Track referral events
 */
export function trackReferralLinkCreated(userId: string, workspaceId: string) {
  return trackEvent(workspaceId, userId, {
    event_type: 'referral',
    event_name: 'referral_link_created',
  });
}

/**
 * Track WhatsApp events
 */
export function trackWhatsAppClicked(
  userId: string,
  workspaceId: string,
  leadId: string,
  templateId?: string
) {
  return trackEvent(workspaceId, userId, {
    event_type: 'whatsapp',
    event_name: 'whatsapp_clicked',
    properties: { leadId, templateId },
  });
}

